import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { projectColorVar } from '../../models/project-colors';

export interface ProjectOption {
  id: string;
  name: string;
}

export type ProjectsState = 'loading' | 'loaded' | 'error';

interface OptionView {
  id: string;
  name: string;
  colorVar: string;
  selected: boolean;
  last: boolean;
}

const LIST_POSITIONS: ConnectedPosition[] = [
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
];

@Component({
  selector: 'app-projekt-filter',
  imports: [TranslatePipe, MatIcon, CdkOverlayOrigin, CdkConnectedOverlay],
  templateUrl: './projekt-filter.component.html',
  styleUrl: './projekt-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjektFilterComponent {
  private readonly injector = inject(Injector);

  projects = input<ProjectOption[]>([]);
  colorOrder = input<readonly string[]>([]);
  selected = input<string[]>([]);
  state = input<ProjectsState>('loaded');

  selectedChange = output<string[]>();

  protected readonly positions = LIST_POSITIONS;
  protected readonly open = signal(false);

  private readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');
  private readonly listbox = viewChild<ElementRef<HTMLElement>>('listbox');

  protected readonly disabled = computed(() => this.state() !== 'loaded' || this.projects().length === 0);

  protected readonly allSelected = computed(() => this.disabled() || this.selected().length === 0);

  private readonly effectiveSelection = computed(() =>
    this.selected().length === 0 ? this.projects().map((project) => project.id) : this.selected(),
  );

  protected readonly options = computed<OptionView[]>(() => {
    const selection = this.effectiveSelection();
    return this.projects().map((project, index) => {
      const selected = selection.includes(project.id);
      return {
        id: project.id,
        name: project.name,
        colorVar: projectColorVar(project.id, this.colorOrder(), index),
        selected,
        last: selected && selection.length === 1,
      };
    });
  });

  protected readonly singleName = computed(() => {
    const selected = this.selected();
    return this.projects().find((project) => project.id === selected[0])?.name ?? '';
  });

  protected selectAll(): void {
    this.selectedChange.emit([]);
  }

  protected toggleOpen(): void {
    if (this.open()) {
      this.close();
      return;
    }
    this.open.set(true);
    afterNextRender(() => this.focusOption(0), { injector: this.injector });
  }

  protected close(): void {
    if (!this.open()) return;
    this.open.set(false);
    this.trigger().nativeElement.focus();
  }

  protected toggle(id: string): void {
    const all = this.projects().map((project) => project.id);
    const basis = this.effectiveSelection();
    const next = basis.includes(id) ? basis.filter((selectedId) => selectedId !== id) : [...basis, id];
    if (next.length === 0) return;
    this.selectedChange.emit(next.length === all.length ? [] : next);
  }

  protected onOptionKeydown(event: KeyboardEvent, index: number, id: string): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggle(id);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.focusOption(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusOption(index - 1);
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  private focusOption(index: number): void {
    const options = this.listbox()?.nativeElement.querySelectorAll<HTMLElement>('[role="option"]');
    if (!options || options.length === 0) return;
    options[Math.max(0, Math.min(index, options.length - 1))].focus();
  }
}
